import { analytics } from "@heycatch/sdk";
import { TrustLine, PrivacyLine } from "@/pages/PublicSite";
import { useState } from "react";
import { Navigate } from "react-router-dom";
import { Sparkles, Lock, Globe2 } from "lucide-react";
import { BrynLogo } from "@/components/BrynLogo";
import { Mascot } from "@/components/Mascot";
import { toast } from "sonner";
import { http, formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

const Feature = ({ icon: Icon, title, text, color }) => (
  <div className="flex gap-4 rise">
    <div className={`h-10 w-10 shrink-0 rounded-xl border border-white/10 flex items-center justify-center ${color}`}>
      <Icon className="h-5 w-5" />
    </div>
    <div>
      <p className="font-display font-medium text-white">{title}</p>
      <p className="text-sm text-neutral-400 leading-relaxed">{text}</p>
    </div>
  </div>
);

function GoogleButton() {
  const google = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/app";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };
  return (
    <Button type="button" variant="outline" onClick={google} data-testid="google-login-btn"
      className="w-full h-11 rounded-full border-white/15 bg-white/5 hover:bg-white/10">
      <svg className="h-4 w-4 mr-2" viewBox="0 0 24 24"><path fill="#EA4335" d="M12 10.2v3.9h5.5c-.2 1.3-1.6 3.8-5.5 3.8-3.3 0-6-2.7-6-6.1s2.7-6.1 6-6.1c1.9 0 3.1.8 3.8 1.5l2.6-2.5C16.8 3.2 14.6 2.2 12 2.2 6.6 2.2 2.3 6.6 2.3 12s4.3 9.8 9.7 9.8c5.6 0 9.3-3.9 9.3-9.5 0-.6-.1-1.1-.2-1.6H12z"/></svg>
      Continue with Google
    </Button>
  );
}

function AuthError({ message }) {
  if (!message) return null;
  return <p className="text-sm text-red-300" role="alert" data-testid="auth-error">{message}</p>;
}

function EmailFields({ mode, form, set }) {
  return (
    <div className="space-y-3">
      {mode === "register" && (
        <Input placeholder="Name" value={form.name} onChange={set("name")} className="h-11 bg-white/5 border-white/10" data-testid="auth-name-input" />
      )}
      <Input type="email" required placeholder="Email" value={form.email} onChange={set("email")} className="h-11 bg-white/5 border-white/10" data-testid="auth-email-input" />
      <Input type="password" required placeholder="Password" value={form.password} onChange={set("password")} className="h-11 bg-white/5 border-white/10" data-testid="auth-password-input" />
    </div>
  );
}

function AuthHero() {
  return (
      <div className="hidden lg:flex flex-col justify-between p-14 border-r border-white/10 relative overflow-hidden">
        <div className="absolute -top-40 -left-40 h-[520px] w-[520px] rounded-full bg-sky-500/10 blur-3xl" />
        <div className="absolute bottom-0 right-0 h-[380px] w-[380px] rounded-full bg-violet-400/10 blur-3xl" />
        <div className="flex items-center gap-2 relative">
          <BrynLogo markClass="h-9 w-9" text="text-xl" />
        </div>
        <div className="relative max-w-lg space-y-10">
          <h1 className="font-display text-5xl lg:text-6xl font-semibold tracking-tight leading-[1.05]">
            A tutor that gets <span className="text-sky-400">smarter</span> with every question.
          </h1>
          <div className="space-y-6">
            <Feature icon={Sparkles} color="text-violet-300 bg-violet-500/10" title="Step by step"
              text="Every answer shows the method, so you can do the next one yourself." />
            <Feature icon={Lock} color="text-emerald-300 bg-emerald-500/10" title="Private memory"
              text="Your own struggles and preferences stay yours, used only to help you." />
            <Feature icon={Globe2} color="text-sky-300 bg-sky-500/10" title="Learns from the web"
              text="When unsure, it researches, cites sources, and remembers what it found." />
          </div>
        </div>
        <p className="text-xs text-neutral-600 relative">Upload homework, notes, PDFs and photos. Get explanations, not just answers.</p>
      </div>
  );
}

function AuthHeading({ mode }) {
  return (
    <div>
      <h2 className="font-display text-3xl font-semibold tracking-tight">{mode === "login" ? "Welcome back" : "Create your free account"}</h2>
      <p className="text-sm text-neutral-400 mt-2">{mode === "login" ? "Pick up where you left off." : "Start learning with a tutor that remembers."}</p>
    </div>
  );
}

export default function AuthPage() {
  const { user, setUser } = useAuth();
  const [mode, setMode] = useState(new URLSearchParams(window.location.search).get("mode") === "login" ? "login" : "register");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  if (user) return <Navigate to="/app" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = await http.post(`/auth/${mode}`, form);
      if (mode === "register") analytics.trackEvent("signup_completed", { method: "email" }, { setOnce: { signup_date: new Date().toISOString(), initial_plan: "free" } });
      setUser(data);
    } catch (err) {
      setError(formatError(err));
      toast.error(formatError(err));
    } finally {
      setBusy(false);
    }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div className="min-h-screen grid lg:grid-cols-[1.1fr_1fr] relative z-10">
      <AuthHero />

      <div className="flex items-center justify-center p-6 sm:p-12">
        <form onSubmit={submit} className="w-full max-w-sm space-y-6 rise" data-testid="auth-form">
          <div className="lg:hidden flex items-center gap-3 mb-6">
            <BrynLogo markClass="h-9 w-9" text="text-xl" />
            <Mascot pose="happy" className="h-12 w-12 ml-auto" eager />
          </div>
          <AuthHeading mode={mode} />
          <GoogleButton />
          <div className="flex items-center gap-3 text-xs text-neutral-500"><div className="h-px flex-1 bg-white/10" />or<div className="h-px flex-1 bg-white/10" /></div>
          <EmailFields mode={mode} form={form} set={set} />
          <AuthError message={error} />
          <Button type="submit" disabled={busy} data-testid="auth-submit-btn"
            className="w-full h-11 rounded-full bg-sky-400 text-white hover:bg-sky-500 font-medium">
            {busy ? "Please wait…" : mode === "login" ? "Log in" : "Create free account"}
          </Button>
          <p className="text-sm text-neutral-400 text-center">
            {mode === "login" ? "New here?" : "Already have an account?"}{" "}
            <button type="button" className="text-sky-400 hover:underline" data-testid="auth-toggle-mode-btn"
              onClick={() => { setMode(mode === "login" ? "register" : "login"); setError(""); }}>
              {mode === "login" ? "Create free account" : "Log in"}
            </button>
          </p>
          {mode === "register" && <div className="space-y-1.5 text-center"><TrustLine /><PrivacyLine /></div>}
        </form>
      </div>
    </div>
  );
}
